import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import qs from "qs";
import { transformFormValuesToQueryParams } from "@/exhibitors/utils";
import type {
  EventEditionConfigurationResponse,
  ExhibitorAssociatedBrandsResponse,
  ExhibitorContactsResponse,
  ExhibitorListFormType,
  ExhibitorListQueryParams,
  ExhibitorProductsResponse,
  ExhibitorProfileResponse,
  ExhibitorResourcesResponse,
  ExhibitorsResponse,
  FeaturedExhibitorsResponse,
  FilterConfigResponse,
  ProductsResponse,
  ResourcesResponse,
  SponsoredExhibitorsResponse,
} from "@/components/site-builder/modules/ExhibitorListModule/types";
import type { Locale } from "@/types";

export function eventEditionConfigurationOptions(
  eventEditionCode: string,
  lang: Locale,
) {
  return queryOptions({
    queryKey: [`/api/v1/editions/${eventEditionCode}?lang=${lang}`],
    select: (data: EventEditionConfigurationResponse) => data.data,
  });
}

export function featuredExhibitorsOptions(
  eventEditionCode: string,
  lang: Locale,
) {
  return queryOptions({
    queryKey: [
      `/api/v1/editions/${eventEditionCode}/featuredlistings?lang=${lang}&booth=true&logo=true`,
    ],
    select: (data: FeaturedExhibitorsResponse) => data.data.items,
  });
}

export function sponsoredExhibitorsOptions(
  eventEditionCode: string,
  lang: Locale,
) {
  return queryOptions({
    queryKey: [`/api/v1/editions/${eventEditionCode}/sponsors?lang=${lang}`],
    select: (data: SponsoredExhibitorsResponse) => data.data.items,
  });
}

export function filterConfigOptions(
  eventSeriesCode: string,
  eventEditionCode: string,
  lang: Locale,
) {
  return queryOptions({
    queryKey: [
      `/api/v1/taxonomy/series/${eventSeriesCode}/category?lang=${lang}&edition=${eventEditionCode}`,
    ],
    select: (data: FilterConfigResponse) => data.data,
  });
}

export function exhibitorsListingOptions(
  host: string,
  eventEditionCode: string,
  getValues: () => ExhibitorListFormType,
  lang: Locale,
) {
  return queryOptions({
    async queryFn(): Promise<ExhibitorsResponse> {
      const query = { lang, ...transformFormValuesToQueryParams(getValues()) };
      const queryString = qs.stringify(query, { addQueryPrefix: true });

      const response = await fetch(
        `${host}/api/v1/editions/${eventEditionCode}/listings${queryString}`,
      );
      return await response.json();
    },
    queryKey: ["exhibitors:exhibitors", host, eventEditionCode, lang],
    placeholderData: keepPreviousData,
    select: (data) => data.data,
  });
}

export function productsListingOptions(
  host: string,
  eventEditionCode: string,
  getValues: () => ExhibitorListFormType,
  lang: Locale,
) {
  return queryOptions({
    queryFn: async (): Promise<ProductsResponse> => {
      const values = getValues();
      const params: ExhibitorListQueryParams = {
        page: values.page,
        limit: values.limit,
        q: values.searchTerm,
      };
      const query = { lang, ...params };
      const queryString = qs.stringify(query, { addQueryPrefix: true });

      const response = await fetch(
        `${host}/api/v1/products/${eventEditionCode}/listings${queryString}`,
      );
      return await response.json();
    },
    queryKey: ["exhibitors:products", host, eventEditionCode, lang],
    placeholderData: keepPreviousData,
    select: (data) => data.data,
  });
}

export function resourcesListingOptions(
  host: string,
  eventEditionCode: string,
  getValues: () => ExhibitorListFormType,
  lang: Locale,
) {
  return queryOptions({
    async queryFn(): Promise<ResourcesResponse> {
      const values = getValues();
      const params: ExhibitorListQueryParams = {
        page: values.page,
        limit: values.limit,
        q: values.searchTerm,
      };
      const query = { lang, ...params };
      const queryString = qs.stringify(query, { addQueryPrefix: true });

      const response = await fetch(
        `${host}/api/v1/resources/${eventEditionCode}/listings${queryString}`,
      );
      return await response.json();
    },
    queryKey: ["exhibitors:resources", host, eventEditionCode, lang],
    placeholderData: keepPreviousData,
    select: (data) => data.data,
  });
}

export function exhibitorProfileOptions(
  slug: string,
  eventEditionCode: string,
  lang: Locale,
) {
  return queryOptions({
    queryKey: [
      `/api/v1/listings/${slug}?lang=${lang}&edition=${eventEditionCode}`,
    ],
    select: (data: ExhibitorProfileResponse) => data.data,
  });
}

export function exhibitorProductsOptions(
  exhibitorSlug: string,
  eventEditionCode: string,
  lang: Locale,
) {
  return queryOptions({
    queryKey: [
      `/api/v1/listings/${exhibitorSlug}/products?lang=${lang}&edition=${eventEditionCode}`,
    ],
    select: (data: ExhibitorProductsResponse) => data.data.items,
  });
}

export function exhibitorResourcesOptions(
  exhibitorSlug: string,
  eventEditionCode: string,
  lang: Locale,
) {
  return queryOptions({
    queryKey: [
      `/api/v1/listings/${exhibitorSlug}/resources?lang=${lang}&edition=${eventEditionCode}`,
    ],
    select: (data: ExhibitorResourcesResponse) => data.data.items,
  });
}

export function exhibitorAssociatedBrandsOptions(
  exhibitorSlug: string,
  eventEditionCode: string,
  lang: Locale,
) {
  return queryOptions({
    queryKey: [
      `/api/v1/listings/${exhibitorSlug}/associatedbrands?lang=${lang}&edition=${eventEditionCode}`,
    ],
    select: (data: ExhibitorAssociatedBrandsResponse) =>
      data.data.associated_brands,
  });
}

export function exhibitorContactsOptions(
  exhibitorSlug: string,
  eventEditionCode: string,
  lang: Locale,
) {
  return queryOptions({
    queryKey: [
      `/api/v1/listings/${exhibitorSlug}/contacts?lang=${lang}&edition=${eventEditionCode}`,
    ],
    select: (data: ExhibitorContactsResponse) => data.data.items,
  });
}
